// The shared-form model used by the form designer (Workbook), the public fill page (Drive) and the tests.
// It mirrors Drive BFF `FormSchema.cs` / `FormValidator.cs` rule for rule: the page checks answers as the user types, the server checks
// them again on submit. Keep the two in step. Pure TypeScript with erasable syntax only (no enums, no imports), so `node --test` can run it.

export const FORM_SCHEMA_VERSION = 2;

export const INPUT_TYPES = [
    "text", "textarea", "richtext", "password", "email", "phone", "url", "number", "date", "time",
    "radio", "select", "multiselect", "checkboxes", "toggle", "rating", "scale", "slider", "file", "photo", "signature", "hidden"
] as const;
export const LAYOUT_TYPES = ["section", "band", "divider", "spacer", "image", "stamp", "submit", "reset", "link"] as const;
export type InputType = (typeof INPUT_TYPES)[number];
export type LayoutType = (typeof LAYOUT_TYPES)[number];
export type ControlType = InputType | LayoutType;

export const PRESETS = ["outlined", "filled", "underline", "comb", "pill", "minimal"] as const;
export type Preset = (typeof PRESETS)[number];
export const LABEL_POSITIONS = ["above", "left", "inside", "floating"] as const;
export type LabelPosition = (typeof LABEL_POSITIONS)[number];
export const CONDITION_OPS = ["equals", "notEquals", "contains", "notEmpty", "empty", "gt", "lt"] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

export interface FormCondition { field: string; op: ConditionOp; value?: string }

export interface FormField {
    id: string;
    type: ControlType;
    title: string;
    help?: string;
    required?: boolean;
    defaultValue?: string;
    placeholder?: string;
    /** Version 1 options (comma separated); kept so old forms load. */
    options?: string;
    choices?: string[];
    allowOther?: boolean;
    min?: number;
    max?: number;
    step?: number;
    wholeNumber?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    patternMessage?: string;
    minValue?: string;
    maxValue?: string;
    minLabel?: string;
    maxLabel?: string;
    maxFileKb?: number;
    accept?: string;
    url?: string;
    showIf?: FormCondition | null;
    // design layer
    span?: number;
    group?: string;
    style?: Preset;
    comb?: boolean;
    color?: string;
    imageData?: string;
    height?: number;
}

export interface FormBanner { title?: string; subtitle?: string; color: string; imageData?: string }
export interface FormTheme { preset: Preset; accent: string; radius: number; labelPosition: LabelPosition; numbered: boolean; background?: string; banner?: FormBanner | null }
export interface FormGroup { id: string; name: string; color: string }
export interface FormSchema { version: number; source?: string; theme: FormTheme; groups: FormGroup[]; fields: FormField[] }

export const DEFAULT_THEME: FormTheme = { preset: "outlined", accent: "#2563eb", radius: 8, labelPosition: "above", numbered: false };

export const MAX_FIELDS = 100;
export const DEFAULT_FILE_KB = 256;
export const HARD_FILE_KB = 1024;

// ---------- palette ----------

export interface ControlInfo { type: ControlType; label: string; group: string; hint: string }
export const CONTROLS: ControlInfo[] = [
    { type: "text", label: "Short answer", group: "Text", hint: "One line of text" },
    { type: "textarea", label: "Paragraph", group: "Text", hint: "Several lines" },
    { type: "richtext", label: "Rich text", group: "Text", hint: "Long text with a bigger box" },
    { type: "password", label: "Masked text", group: "Text", hint: "Characters shown as dots" },
    { type: "email", label: "Email", group: "Text", hint: "Checked as an address" },
    { type: "phone", label: "Phone", group: "Text", hint: "Digits, spaces, + ( ) -" },
    { type: "url", label: "Web address", group: "Text", hint: "http or https link" },
    { type: "radio", label: "Radio buttons", group: "Choice", hint: "Pick one, all shown" },
    { type: "select", label: "Dropdown", group: "Choice", hint: "Pick one from a list" },
    { type: "multiselect", label: "Multi-select", group: "Choice", hint: "Pick several from a list" },
    { type: "checkboxes", label: "Checkbox group", group: "Choice", hint: "Tick any that apply" },
    { type: "toggle", label: "Yes / No", group: "Choice", hint: "A switch" },
    { type: "number", label: "Number", group: "Numbers", hint: "With minimum, maximum and step" },
    { type: "rating", label: "Star rating", group: "Numbers", hint: "1 to 5 stars" },
    { type: "scale", label: "Linear scale", group: "Numbers", hint: "For example 1 to 10" },
    { type: "slider", label: "Slider", group: "Numbers", hint: "Drag to a value" },
    { type: "date", label: "Date", group: "Date and time", hint: "A calendar date" },
    { type: "time", label: "Time", group: "Date and time", hint: "hh:mm" },
    { type: "file", label: "File upload", group: "Files", hint: "Small files, size limited" },
    { type: "photo", label: "Photo box", group: "Files", hint: "A picture, shown in a box" },
    { type: "signature", label: "Signature", group: "Files", hint: "Type or draw" },
    { type: "hidden", label: "Hidden field", group: "Files", hint: "Carries a value, not shown" },
    { type: "section", label: "Section heading", group: "Layout", hint: "A title with optional text" },
    { type: "band", label: "Section band", group: "Layout", hint: "A coloured bar with a title" },
    { type: "divider", label: "Divider", group: "Layout", hint: "A line" },
    { type: "spacer", label: "Spacer", group: "Layout", hint: "Empty space" },
    { type: "image", label: "Picture", group: "Layout", hint: "An image block" },
    { type: "stamp", label: "Stamp box", group: "Layout", hint: "A bordered box with a caption" },
    { type: "submit", label: "Submit button", group: "Buttons", hint: "Sends the form" },
    { type: "reset", label: "Reset button", group: "Buttons", hint: "Clears the answers" },
    { type: "link", label: "Link button", group: "Buttons", hint: "Opens a web address" }
];

// ---------- types and upgrade ----------

export const isInput = (t: string): t is InputType => (INPUT_TYPES as readonly string[]).includes(t);
export const isLayout = (t: string): t is LayoutType => (LAYOUT_TYPES as readonly string[]).includes(t);
export const hasChoices = (t: string) => t === "radio" || t === "select" || t === "multiselect" || t === "checkboxes";
export const isMulti = (t: string) => t === "multiselect" || t === "checkboxes";
export const isFile = (t: string) => t === "file" || t === "photo";

/** Version 1 names map onto the canonical ids. */
export function canonicalType(t: string | undefined): string {
    switch ((t ?? "").trim()) {
        case "Short answer": return "text";
        case "Paragraph": return "textarea";
        case "Number": case "Whole number": return "number";
        case "Date": return "date";
        case "Email": return "email";
        case "Multiple choice": return "radio";
        case "Dropdown": return "select";
        case "Checkbox (Yes/No)": return "toggle";
        default: return (t ?? "").trim();
    }
}

/** Reads version 1 (an array of {title,type,options,required}), version 2 or nothing; always returns a current schema with defaults filled in. */
export function upgradeSchema(input: unknown): FormSchema {
    let raw: Record<string, unknown> = {};
    let fields: unknown[] = [];
    if (Array.isArray(input)) fields = input;
    else if (input && typeof input === "object") { raw = input as Record<string, unknown>; if (Array.isArray(raw.fields)) fields = raw.fields; else if (Array.isArray(raw.questions)) fields = raw.questions; }
    const theme = { ...DEFAULT_THEME, ...((raw.theme && typeof raw.theme === "object" ? raw.theme : {}) as Partial<FormTheme>) };
    const groups = (Array.isArray(raw.groups) ? raw.groups : []) as FormGroup[];
    const used = new Set<string>();
    const out: FormField[] = fields.map((x, i) => {
        const f = { ...((x && typeof x === "object" ? x : {}) as Record<string, unknown>) } as unknown as FormField & { type: string };
        if ((f.type as string) === "Whole number") f.wholeNumber = true;
        f.type = canonicalType(f.type) as ControlType;
        if (!f.type) f.type = "text";
        f.title = typeof f.title === "string" ? f.title : "";
        let id = typeof f.id === "string" && f.id.trim() ? f.id.trim() : `q${i + 1}`;
        const base = id; let n = 2;
        while (used.has(id)) id = `${base}_${n++}`;
        used.add(id);
        f.id = id;
        return f as FormField;
    });
    return { version: FORM_SCHEMA_VERSION, ...(typeof raw.source === "string" ? { source: raw.source } : {}), theme, groups, fields: out };
}

export function choicesOf(f: FormField): string[] {
    const list = f.choices && f.choices.length ? f.choices : (f.options ?? "").split(",");
    return list.map((x) => x.trim()).filter(Boolean);
}
export const splitMulti = (v: string): string[] => v.split("\n").map((x) => x.trim()).filter(Boolean);
export const joinMulti = (v: string[]): string => v.join("\n");

/** A new field with sensible settings for its type and an id not yet used. */
export function newField(type: ControlType, taken: Iterable<string>): FormField {
    const ids = new Set(taken);
    let n = ids.size + 1;
    while (ids.has(`q${n}`)) n++;
    const info = CONTROLS.find((c) => c.type === type);
    const f: FormField = { id: `q${n}`, type, title: info?.label ?? type };
    switch (type) {
        case "radio": case "select": case "multiselect": case "checkboxes": f.choices = ["Option 1", "Option 2", "Option 3"]; break;
        case "rating": f.max = 5; break;
        case "scale": f.min = 1; f.max = 10; f.minLabel = "Low"; f.maxLabel = "High"; break;
        case "slider": f.min = 0; f.max = 100; f.step = 1; f.defaultValue = "50"; break;
        case "file": f.maxFileKb = DEFAULT_FILE_KB; break;
        case "photo": f.maxFileKb = DEFAULT_FILE_KB; f.height = 140; break;
        case "textarea": case "richtext": f.maxLength = 2000; break;
        case "link": f.url = "https://example.com"; f.title = "Open link"; break;
        case "submit": f.title = "Submit"; break;
        case "reset": f.title = "Reset"; break;
        case "spacer": f.title = ""; f.height = 24; break;
        case "divider": f.title = ""; break;
        case "band": f.title = "Section"; f.color = "#dbeafe"; break;
        case "stamp": f.title = "Stamp"; f.height = 90; break;
        case "hidden": f.title = "Hidden value"; break;
        case "image": f.title = ""; break;
        default: break;
    }
    if (isLayout(type)) f.span = 6;
    return f;
}

// ---------- conditional visibility ----------

export function matches(c: Pick<FormCondition, "op" | "value">, answer: string, source?: Pick<FormField, "type">): boolean {
    const a = answer.trim();
    const want = (c.value ?? "").trim().toLowerCase();
    const parts = source && isMulti(source.type) ? splitMulti(a) : [a];
    switch (c.op) {
        case "empty": return a === "";
        case "notEmpty": return a !== "";
        case "equals": return parts.some((p) => p.toLowerCase() === want);
        case "notEquals": return !parts.some((p) => p.toLowerCase() === want);
        case "contains": return want !== "" && parts.some((p) => p.toLowerCase().includes(want));
        case "gt": case "lt": {
            const x = Number(a), y = Number((c.value ?? "").trim());
            if (a === "" || (c.value ?? "").trim() === "" || !Number.isFinite(x) || !Number.isFinite(y)) return false;
            return c.op === "gt" ? x > y : x < y;
        }
        default: return false;
    }
}

/** Which fields are shown for these answers. Hidden by a rule, by a hidden controlling field, by an unknown field or by a loop. */
export function visibleFlags(fields: readonly FormField[], answers: readonly (string | undefined)[]): boolean[] {
    const index = new Map(fields.map((f, i) => [f.id, i] as const));
    const state = new Array<number>(fields.length).fill(0); // 0 unknown, 1 computing, 2 shown, 3 hidden
    const ev = (i: number): boolean => {
        if (state[i]! >= 2) return state[i] === 2;
        if (state[i] === 1) return false;
        state[i] = 1;
        const c = fields[i]!.showIf;
        let shown = true;
        if (c && c.field) {
            const src = index.get(c.field);
            if (src === undefined || src === i || state[src] === 1) shown = false;
            else shown = ev(src) && matches(c, answers[src] ?? "", fields[src]);
        }
        state[i] = shown ? 2 : 3;
        return shown;
    };
    return fields.map((_, i) => ev(i));
}

// ---------- answers ----------

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9 ()\-.]{5,25}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DANGEROUS = new Set(["exe", "com", "scr", "pif", "msi", "msp", "mst", "cpl", "cab", "dll", "sys", "drv", "ocx", "gadget", "scf", "lnk", "inf", "ins", "isp", "reg", "app", "msc", "jar", "bat", "cmd", "vb", "vbs", "vbe", "js", "jse", "ws", "wsf", "wsc", "wsh", "ps1", "ps1xml", "ps2", "ps2xml", "psc1", "psc2", "psm1", "hta", "htc", "sh", "bash", "csh", "ksh", "zsh", "py", "pyc", "pyo", "pl", "rb", "php", "phtml", "asp", "aspx", "jsp", "url", "shb", "shs", "sct", "pcd", "chm", "hlp", "deb", "rpm", "dmg", "pkg", "apk", "run", "bin", "out", "elf", "so", "class", "swf"]);

const fmt = (n: number) => String(Math.round(n * 100) / 100);

export function validDate(v: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m) return false;
    const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
    return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!;
}

function lengthAndPattern(f: FormField, v: string): string | null {
    if (f.minLength != null && v.length < f.minLength) return `Enter at least ${f.minLength} characters.`;
    if (f.maxLength != null && v.length > f.maxLength) return `Enter at most ${f.maxLength} characters.`;
    if (f.pattern) {
        try { if (!new RegExp(`^(?:${f.pattern})$`).test(v)) return f.patternMessage?.trim() ? f.patternMessage.slice(0, 200) : "The answer is not in the expected format."; } catch { /* refused when publishing */ }
    }
    return null;
}

/** Problem with one answer as a sentence, or null. Layout fields have no answer. */
export function checkAnswer(f: FormField, raw: string): string | null {
    const type = canonicalType(f.type);
    const v = raw.trim();
    if (v === "") return f.required ? "This question is required." : null;
    switch (type) {
        case "text": case "textarea": case "richtext": case "password": case "hidden": return lengthAndPattern(f, v);
        case "email": return EMAIL.test(v) ? lengthAndPattern(f, v) : "Enter an email address.";
        case "phone": return PHONE.test(v) ? lengthAndPattern(f, v) : "Enter a phone number.";
        case "url": {
            try { const u = new URL(v); if (u.protocol !== "http:" && u.protocol !== "https:") throw new Error("scheme"); } catch { return "Enter a web address starting with http:// or https://."; }
            return lengthAndPattern(f, v);
        }
        case "number": case "slider": {
            const n = Number(v);
            if (!Number.isFinite(n)) return "Enter a number.";
            if (f.wholeNumber && !Number.isInteger(n)) return "Enter a whole number.";
            if (f.min != null && n < f.min) return `Enter a number of at least ${fmt(f.min)}.`;
            if (f.max != null && n > f.max) return `Enter a number of at most ${fmt(f.max)}.`;
            if (f.step && f.step > 0 && Math.abs(Math.round((n - (f.min ?? 0)) / f.step) * f.step - (n - (f.min ?? 0))) > 1e-9) return `Use steps of ${fmt(f.step)}.`;
            return null;
        }
        case "rating": case "scale": {
            const lo = type === "rating" ? 1 : Math.trunc(f.min ?? 1);
            const hi = type === "rating" ? Math.trunc(f.max ?? 5) : Math.trunc(f.max ?? 10);
            return /^-?\d+$/.test(v) && +v >= lo && +v <= hi ? null : `Choose a value from ${lo} to ${hi}.`;
        }
        case "date":
            if (!validDate(v)) return "Enter a date as yyyy-mm-dd.";
            if (f.minValue && v < f.minValue) return `The date must be on or after ${f.minValue}.`;
            if (f.maxValue && v > f.maxValue) return `The date must be on or before ${f.maxValue}.`;
            return null;
        case "time":
            if (!TIME.test(v)) return "Enter a time as hh:mm.";
            if (f.minValue && v < f.minValue) return `The time must be ${f.minValue} or later.`;
            if (f.maxValue && v > f.maxValue) return `The time must be ${f.maxValue} or earlier.`;
            return null;
        case "radio": case "select":
            return choicesOf(f).includes(v) || (f.allowOther && v.length <= 200 && !v.includes("\n")) ? null : "Pick one of the options.";
        case "multiselect": case "checkboxes": {
            const picks = splitMulti(v), set = choicesOf(f);
            for (const p of picks) if (!set.includes(p) && !(f.allowOther && p.length <= 200)) return `“${p.slice(0, 40)}” is not one of the options.`;
            if (new Set(picks).size !== picks.length) return "Each option can be chosen once.";
            if (f.min != null && picks.length < f.min) return `Choose at least ${fmt(f.min)}.`;
            if (f.max != null && picks.length > f.max) return `Choose at most ${fmt(f.max)}.`;
            return null;
        }
        case "toggle": return v === "Yes" || v === "No" ? null : "Choose Yes or No.";
        case "file": case "photo": return checkFileAnswer(f, v, type === "photo");
        case "signature":
            if (v.startsWith("typed:")) return v.length > 6 && v.length <= 206 ? null : "Type your name to sign.";
            if (v.startsWith("drawn:")) return /^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(v.slice(6)) ? null : "The drawn signature is not valid.";
            return "Sign by typing your name or drawing.";
        default: return null;
    }
}

export function checkFileAnswer(f: FormField, v: string, imageOnly: boolean): string | null {
    let a: { name?: string; data?: string };
    try { a = JSON.parse(v) as typeof a; } catch { return "The file could not be read."; }
    if (!a || !a.name || !a.data) return "The file could not be read.";
    if (a.name.length > 200 || /[\\/\0\r\n]/.test(a.name)) return "The file name is not valid.";
    const ext = a.name.includes(".") ? a.name.split(".").pop()!.toLowerCase() : "";
    if (DANGEROUS.has(ext)) return "This type of file is not allowed.";
    const maxKb = Math.min(f.maxFileKb ?? DEFAULT_FILE_KB, HARD_FILE_KB);
    const bytes = Math.floor((a.data.length * 3) / 4) - (a.data.endsWith("==") ? 2 : a.data.endsWith("=") ? 1 : 0);
    if (bytes > maxKb * 1024) return `The file is larger than ${maxKb} KB.`;
    if (imageOnly && !["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "Choose a PNG, JPEG, GIF or WebP picture.";
    if (f.accept && f.accept.trim()) {
        const ok = f.accept.split(",").map((x) => x.trim().replace(/^\./, "").toLowerCase()).filter(Boolean).includes(ext);
        if (!ok) return `Allowed file types: ${f.accept}.`;
    }
    return null;
}

/** Checks a whole submission. Hidden and layout fields are skipped and come back empty; `errors[i]` is the problem of field i. */
export function checkAll(schema: Pick<FormSchema, "fields">, answers: readonly (string | undefined)[]): { errors: (string | null)[]; cleaned: string[]; visible: boolean[] } {
    const visible = visibleFlags(schema.fields, answers);
    const errors: (string | null)[] = [];
    const cleaned: string[] = [];
    schema.fields.forEach((f, i) => {
        if (!visible[i] || !isInput(f.type)) { errors.push(null); cleaned.push(""); return; }
        const raw = answers[i] ?? "";
        errors.push(checkAnswer(f, raw));
        cleaned.push(isMulti(f.type) ? joinMulti(splitMulti(raw)) : raw.trim());
    });
    return { errors, cleaned, visible };
}

/** The answers a fresh form starts with: each field's default value. */
export function defaultAnswers(fields: readonly FormField[]): string[] {
    return fields.map((f) => (isInput(f.type) ? f.defaultValue ?? "" : ""));
}

// ---------- design helpers ----------

export const effectivePreset = (theme: FormTheme, f: FormField): Preset => (f.style && (PRESETS as readonly string[]).includes(f.style) ? f.style : theme.preset);

export function groupOf(schema: Pick<FormSchema, "groups">, f: FormField): FormGroup | null {
    return f.group ? schema.groups.find((g) => g.id === f.group) ?? null : null;
}

/** "#rrggbb" and an opacity as `rgba(...)`; anything that is not a hex colour comes back unchanged. */
export function tint(hex: string, alpha: number): string {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (!m) return hex;
    return `rgba(${parseInt(m[1]!, 16)}, ${parseInt(m[2]!, 16)}, ${parseInt(m[3]!, 16)}, ${alpha})`;
}

/** Text colour (black or white) that reads on this background. */
export function readableOn(hex: string): string {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (!m) return "#111827";
    const y = (parseInt(m[1]!, 16) * 299 + parseInt(m[2]!, 16) * 587 + parseInt(m[3]!, 16) * 114) / 1000;
    return y >= 150 ? "#111827" : "#ffffff";
}

/** Question numbers for the input fields that are shown ("1.", "2.", ...); null for layout and hidden fields. */
export function questionNumbers(fields: readonly FormField[], visible?: readonly boolean[]): (number | null)[] {
    let n = 0;
    return fields.map((f, i) => (isInput(f.type) && f.type !== "hidden" && (!visible || visible[i]) ? ++n : null));
}

/** Boxes of a comb field: the maximum length; a date is 8 digits (DD MM YYYY). */
export function combBoxes(f: FormField): number {
    return f.type === "date" ? 8 : Math.max(1, Math.min(40, f.maxLength ?? 10));
}

/** Date answer yyyy-mm-dd -> the 8 digits of DD MM YYYY, and back. */
export function dateToDigits(v: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    return m ? `${m[3]}${m[2]}${m[1]}` : "";
}
export function digitsToDate(d: string): string {
    return /^\d{8}$/.test(d) ? `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}` : "";
}

// ---------- publish-time checks (same rules as the server) ----------

const HEX = /^#[0-9a-fA-F]{6}$/;
const IMG = /^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/=]+$/;
export const MAX_IMAGE_CHARS = 400_000;

export function validateSchema(s: FormSchema): string | null {
    if (s.fields.length === 0) return "A form needs at least one field.";
    if (s.fields.length > MAX_FIELDS) return `A form can have at most ${MAX_FIELDS} fields.`;
    if (!s.fields.some((f) => isInput(f.type) && f.type !== "hidden")) return "A form needs at least one question people can answer.";
    const th = s.theme;
    if (!(PRESETS as readonly string[]).includes(th.preset)) return `Unknown style “${th.preset}”.`;
    if (!(LABEL_POSITIONS as readonly string[]).includes(th.labelPosition)) return `Unknown label position “${th.labelPosition}”.`;
    if (!HEX.test(th.accent)) return "The accent colour must be like #2563eb.";
    if (th.background && !HEX.test(th.background)) return "The page colour must be like #f8fafc.";
    if (!(th.radius >= 0 && th.radius <= 32)) return "The corner radius must be between 0 and 32.";
    if (th.banner) {
        if (!HEX.test(th.banner.color)) return "The banner colour must be like #1d4ed8.";
        if ((th.banner.title?.length ?? 0) > 200 || (th.banner.subtitle?.length ?? 0) > 500) return "The banner text is too long.";
        if (th.banner.imageData && (th.banner.imageData.length > MAX_IMAGE_CHARS || !IMG.test(th.banner.imageData))) return "The banner picture must be a small PNG, JPEG, WebP or GIF.";
    }
    if (s.groups.length > 12) return "At most 12 colour groups.";
    const gids = new Set<string>();
    for (const g of s.groups) {
        if (!g.id || g.id.length > 40 || gids.has(g.id)) return "Every colour group needs a unique id.";
        gids.add(g.id);
        if (!HEX.test(g.color)) return `The colour of group “${g.name}” must be like #f97316.`;
    }
    const ids = new Set(s.fields.map((f) => f.id));
    for (const f of s.fields) {
        const t = f.type;
        if (!isInput(t) && !isLayout(t)) return `Unknown control type “${t}”.`;
        if (f.id.length > 40 || !/^[A-Za-z][A-Za-z0-9_-]*$/.test(f.id)) return `“${f.id}” is not a valid field id.`;
        const needsTitle = !["divider", "spacer", "hidden", "image"].includes(t);
        if (needsTitle && !f.title.trim()) return "Every field needs a label.";
        if (f.title.length > 300 || (f.help?.length ?? 0) > 1000 || (f.placeholder?.length ?? 0) > 200) return `“${f.title}” has text that is too long.`;
        if (f.span != null && !(f.span >= 1 && f.span <= 6)) return "A field spans 1 to 6 columns.";
        if (f.group && !gids.has(f.group)) return `“${f.title}” uses a colour group that does not exist.`;
        if (f.style && !(PRESETS as readonly string[]).includes(f.style)) return `Unknown style “${f.style}”.`;
        if (f.color && !HEX.test(f.color)) return `The colour of “${f.title}” must be like #f97316.`;
        if (f.height != null && !(f.height >= 10 && f.height <= 600)) return "A box is 10 to 600 px high.";
        if (f.imageData && (f.imageData.length > MAX_IMAGE_CHARS || !IMG.test(f.imageData))) return "A picture must be a small PNG, JPEG, WebP or GIF.";
        if (t === "image" && !f.imageData) return "A picture block needs a picture.";
        if (hasChoices(t)) {
            const ch = choicesOf(f);
            if (ch.length === 0 && !f.allowOther) return `“${f.title}” needs options.`;
            if (ch.length > 200 || ch.some((c) => c.length > 200 || c.includes("\n"))) return `“${f.title}” has too many or too long options.`;
            if (new Set(ch).size !== ch.length) return `“${f.title}” lists an option twice.`;
        }
        if (f.min != null && f.max != null && f.min > f.max) return `“${f.title}”: the minimum is above the maximum.`;
        if ((f.minLength != null && f.minLength < 0) || (f.maxLength != null && f.maxLength < 0) || (f.minLength != null && f.maxLength != null && f.minLength > f.maxLength)) return `“${f.title}”: check the minimum and maximum length.`;
        if (f.step != null && f.step <= 0) return `“${f.title}”: the step must be above zero.`;
        if (f.comb && t !== "date" && !(f.maxLength != null && f.maxLength >= 1 && f.maxLength <= 40)) return `“${f.title}”: a box-per-character field needs a maximum length of 1 to 40.`;
        if (f.comb && !["text", "number", "date", "phone"].includes(t)) return `“${f.title}”: only text, number, phone and date fields can be boxes.`;
        if (t === "rating" && (f.max ?? 5) > 10) return "A rating has at most 10 stars.";
        if (t === "scale" && ((f.max ?? 10) - (f.min ?? 1) < 1 || (f.max ?? 10) - (f.min ?? 1) > 20)) return "A scale has 2 to 21 steps.";
        if (t === "slider" && (f.min == null || f.max == null)) return `“${f.title}”: a slider needs a minimum and a maximum.`;
        if (f.pattern) {
            if (f.pattern.length > 300) return `“${f.title}”: the pattern is too long.`;
            try { new RegExp(`^(?:${f.pattern})$`); } catch { return `“${f.title}”: the pattern is not a valid regular expression.`; }
        }
        if (t === "link") { try { const u = new URL(f.url ?? ""); if (u.protocol !== "http:" && u.protocol !== "https:") throw new Error(); } catch { return `“${f.title}”: the link must start with http:// or https://.`; } }
        if (f.maxFileKb != null && !(f.maxFileKb >= 1 && f.maxFileKb <= HARD_FILE_KB)) return `A file can be at most ${HARD_FILE_KB} KB.`;
        if (f.showIf && f.showIf.field) {
            if (!ids.has(f.showIf.field) || f.showIf.field === f.id) return `“${f.title}”: its show-when rule points at a field that does not exist.`;
            if (!(CONDITION_OPS as readonly string[]).includes(f.showIf.op)) return `“${f.title}”: unknown rule “${f.showIf.op}”.`;
        }
    }
    const next = new Map<string, string>();
    for (const f of s.fields) if (f.showIf?.field) next.set(f.id, f.showIf.field);
    for (const start of next.keys()) {
        const seen = new Set([start]);
        let cur = start;
        for (;;) { const n = next.get(cur); if (n === undefined) break; if (seen.has(n)) return "The show-when rules form a loop."; seen.add(n); cur = n; }
    }
    return null;
}

// ---------- delivery targets (shapes of the BFF API) ----------

export type TargetKind = "workbook" | "api" | "postgres";
export interface FormTargetView {
    id: string; kind: TargetKind; name: string; enabled: boolean;
    fileId?: string | null; fileName?: string | null;
    url?: string | null; method: string; headers: Array<{ name: string; hasValue: boolean }>; hasSecret: boolean;
    hasConnection: boolean; connectionSummary?: string | null; schema: string; table?: string | null; columns: Array<{ source: string; column: string }>;
}
export interface FormTargetInput {
    id?: string; kind: TargetKind; name?: string; enabled: boolean; fileName?: string;
    url?: string; method?: string; headers?: Array<{ name: string; value?: string }>; secret?: string; clearSecret?: boolean;
    connection?: string; schema?: string; table?: string; columns?: Array<{ source: string; column: string }>;
}
export interface DeliveryRow {
    id: string; responseId: string; targetId: string; targetKind: TargetKind; targetName: string; status: "pending" | "ok" | "failed";
    attempts: number; httpStatus?: number | null; error?: string | null; createdAt: string; updatedAt: string; nextAttemptAt?: string | null; deliveredAt?: string | null;
}
